import { Button } from '@evaneos/b2b-ui/legacy';
import { ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ERROR, PENDING, SUCCESS } from '../../../design-system/constants';
import { Dropdown } from '../../../design-system/Dropdown/ControlledDropdown';

import { Delete } from '../../../design-system/Icons/Delete';
import { MoreVert } from '../../../design-system/Icons/MoreVert';
import { Search } from '../../../design-system/Icons/Search';
import { Loader } from '../../../design-system/Loader';
import {
    deleteMessageTemplateRequest,
    getAllMessageTemplates,
    getAllMessageTemplatesRequest,
    getMessageTemplateSearchIndex,
    getMessageTemplateStatus,
} from '../../../resources/message-template';
import { MessageTemplate } from '../../../resources/message-template/types';
import { useTrackPageViewAmplitude } from '../../../shared/analytics';
import { useAppDispatch, useAppSelector } from '../../../shared/redux';
import { search } from '../../../shared/utilities/search';
import { ErrorOverlay } from '../../Pipeline/components/Error';

import s from '../MessageTemplates.css';

import { NoMessageTemplates } from './NoMessageTemplates';

export const MessageTemplateList = (): JSX.Element => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const messageTemplates = useAppSelector(getAllMessageTemplates);
    const status = useAppSelector(getMessageTemplateStatus);
    const messageTemplateSearchIndex = useAppSelector(getMessageTemplateSearchIndex);

    useEffect(() => {
        dispatch(getAllMessageTemplatesRequest());
    }, [dispatch]);

    const [searchResultIds, setSearchResultIds] = useState<string[]>([]);
    const [searchQuery, handleSearchQuery] = useState<string>('');

    useTrackPageViewAmplitude((ampli, defaultProperties) =>
        ampli.viewedPageTemplate(defaultProperties),
    );

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        handleSearchQuery(e.target.value);
        const results = search(e.target.value, messageTemplateSearchIndex);
        setSearchResultIds(results);
    };

    const filteredMessageTemplates: MessageTemplate[] =
        searchQuery.length >= 2
            ? // filter(Boolean) is used when we search and remove an element, the searchResultIds are not updated
              searchResultIds.map((id) => messageTemplates[id]).filter(Boolean)
            : Object.values(messageTemplates);
    const renderTemplates = (): JSX.Element => (
        <div className={s.messageTemplatesList} data-testid="message-templates-list">
            {filteredMessageTemplates.map((messageTemplate) => (
                <div className={s.messageTemplate} key={messageTemplate.id}>
                    <Link
                        to={{
                            pathname: '/templates/edit/' + messageTemplate.id,
                        }}
                        replace
                    >
                        <p
                            className={s.messageTemplateTitle}
                            data-testid={`message-template-${messageTemplate.id}`}
                        >
                            {messageTemplate.title}
                        </p>
                    </Link>
                    <Dropdown
                        className={s.deleteContainer}
                        buttonClass={s.button}
                        button={() => (
                            <MoreVert
                                color="var(--ink-light)"
                                data-testid={`delete-button-${messageTemplate.id}`}
                            />
                        )}
                    >
                        <div
                            className={s.deleteItem}
                            data-testid={`delete-message-template-${messageTemplate.id}`}
                            onClick={() =>
                                dispatch(deleteMessageTemplateRequest(messageTemplate.id))
                            }
                        >
                            <Delete color="var(--ink)" />
                            <p className="body">
                                <FormattedMessage id="templates.bo.delete" />
                            </p>
                        </div>
                    </Dropdown>
                </div>
            ))}
        </div>
    );

    const displayContentByStatus = (): JSX.Element => {
        switch (status) {
            case PENDING:
                return (
                    <div className={s.centerContainer}>
                        <Loader className={s.loader} />
                    </div>
                );
            case SUCCESS:
                return Object.keys(messageTemplates).length === 0 ? (
                    <div className={s.centerContainer} data-testid="empty-message-templates">
                        <NoMessageTemplates />
                    </div>
                ) : (
                    renderTemplates()
                );
            case ERROR:
                return <ErrorOverlay onRefresh={() => dispatch(getAllMessageTemplatesRequest())} />;
            default:
                return <div />;
        }
    };
    return (
        <div className={s.listContainer}>
            <div className={s.listHeader}>
                <h1>
                    <FormattedMessage id="templates.bo.title" />
                </h1>
                <div>
                    <div className={s.searchInput}>
                        <Search className={s.searchIcon} color="var(--ink-light)" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearch}
                            placeholder={formatMessage({
                                id: 'dossier.search_template',
                            })}
                            data-testid="message-template-search-input"
                        />
                    </div>
                    <Link
                        to={{ pathname: '/templates/new' }}
                        data-testid="message-templates-new"
                        replace
                    >
                        <Button>
                            <FormattedMessage id="templates.bo.new" />
                        </Button>
                    </Link>
                </div>
            </div>
            {displayContentByStatus()}
        </div>
    );
};
